import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  Loader2,
  TrendingUp,
  BarChart3,
  Lightbulb,
  Calendar,
  ChevronRight,
  ExternalLink,
  Sparkles,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { motion } from 'framer-motion';

// Color palette for charts
const CHART_COLORS = ['#65a30d', '#84cc16', '#a3e635', '#bef264', '#d9f99d', '#ecfccb'];

// Stat card color classes
const getStatColorClasses = (color) => {
  const colors = {
    lime: {
      bg: 'bg-gradient-to-br from-lime-50 to-lime-100',
      border: 'border-lime-200',
      text: 'text-lime-700',
      value: 'text-lime-900'
    },
    blue: {
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100',
      border: 'border-blue-200',
      text: 'text-blue-700',
      value: 'text-blue-900'
    },
    amber: {
      bg: 'bg-gradient-to-br from-amber-50 to-amber-100',
      border: 'border-amber-200',
      text: 'text-amber-700',
      value: 'text-amber-900'
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-50 to-purple-100',
      border: 'border-purple-200',
      text: 'text-purple-700',
      value: 'text-purple-900'
    }
  };
  return colors[color] || colors.lime;
};

// Animated stat card component
const StatCard = ({ stat, index }) => {
  const colors = getStatColorClasses(stat.color);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`p-6 rounded-2xl border ${colors.bg} ${colors.border} shadow-sm hover:shadow-md transition-shadow`}
    >
      <p className={`text-4xl font-bold ${colors.value} mb-1`}>{stat.value}</p>
      <p className={`text-sm ${colors.text}`}>{stat.label}</p>
      {stat.trend && (
        <p className={`text-xs ${colors.text} opacity-75 mt-2 flex items-center gap-1`}>
          <TrendingUp className="h-3 w-3" />
          {stat.trend}
        </p>
      )}
    </motion.div>
  );
};

// Chart component
const ChartBlock = ({ chart, index }) => {
  const renderChart = () => {
    if (!chart.data || chart.data.length === 0) return null;

    if (chart.chart_type === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={chart.data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              labelLine={false}
            >
              {chart.data.map((_, i) => (
                <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chart.chart_type === 'horizontal_bar') {
      return (
        <div className="space-y-3">
          {chart.data.map((item, i) => {
            const maxValue = Math.max(...chart.data.map(d => d.value));
            const width = (item.value / maxValue) * 100;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-slate-600 w-28 truncate">{item.name}</span>
                <div className="flex-1 h-8 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${width}%` }}
                    transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                  />
                </div>
                <span className="text-sm font-semibold text-slate-900 w-12 text-right">{item.value}</span>
              </div>
            );
          })}
        </div>
      );
    }

    // Default: bar chart
    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chart.data} layout="vertical">
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
          <Tooltip 
            contentStyle={{ 
              borderRadius: '12px', 
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
            }}
          />
          <Bar dataKey="value" radius={[0, 8, 8, 0]}>
            {chart.data.map((_, i) => (
              <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 + index * 0.1 }}
    >
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-lime-600" />
            {chart.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderChart()}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Insight component
const InsightBlock = ({ insight, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.4 + index * 0.1 }}
      className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200"
    >
      <h3 className="font-semibold text-amber-900 flex items-center gap-2 mb-3">
        <Lightbulb className="h-5 w-5" />
        {insight.title}
      </h3>
      <p className="text-amber-800 leading-relaxed">{insight.body}</p>
      {insight.highlight && (
        <div className="mt-4 p-3 bg-white/50 rounded-xl border border-amber-200">
          <p className="text-sm font-medium text-amber-900 italic">&ldquo;{insight.highlight}&rdquo;</p>
        </div>
      )}
    </motion.div>
  );
};

// Empty state component
const EmptyState = () => (
  <div className="text-center py-16">
    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
      <TrendingUp className="h-10 w-10 text-slate-300" />
    </div>
    <h2 className="text-xl font-heading text-slate-900 mb-2">No Market Pulse Available</h2>
    <p className="text-slate-500 max-w-md mx-auto mb-6">
      We&apos;re preparing the latest insights on the life sciences job market. 
      Check back soon for curated trends and data.
    </p>
    <Link to="/dashboard">
      <Button variant="outline">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>
    </Link>
  </div>
);

export default function MarketPulsePage() {
  const { authAxios, logout } = useAuth();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPulse = useCallback(async () => {
    try {
      const response = await authAxios.get('/candidates/market-pulse');
      setPulse(response.data.pulse);
    } catch (error) {
      console.error('Failed to load market pulse:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load market pulse');
      }
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadPulse();
  }, [loadPulse]);

  const handleCTA = () => {
    if (!pulse?.cta_url) return;
    
    if (pulse.cta_url.startsWith('http')) {
      window.open(pulse.cta_url, '_blank');
    } else {
      navigate(pulse.cta_url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!pulse ? (
          <EmptyState />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Header Section */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime-100 text-lime-700 text-sm font-medium mb-4"
              >
                <Activity className="h-4 w-4" />
                Weekly Market Report
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl md:text-4xl font-heading text-slate-900 mb-2"
              >
                {pulse.title}
              </motion.h1>
              
              {pulse.subtitle && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-lg text-slate-500"
                >
                  {pulse.subtitle}
                </motion.p>
              )}
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-sm text-slate-400 mt-2 flex items-center justify-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                Published {format(new Date(pulse.publish_at || pulse.created_at), 'MMMM d, yyyy')}
              </motion.p>
            </div>

            {/* Introduction */}
            {pulse.intro_text && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm"
              >
                <p className="text-slate-600 leading-relaxed text-lg">{pulse.intro_text}</p>
              </motion.div>
            )}

            {/* Stat Cards */}
            {pulse.stat_cards?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {pulse.stat_cards.map((stat, index) => (
                  <StatCard key={index} stat={stat} index={index} />
                ))}
              </div>
            )}

            {/* Charts */}
            {pulse.charts?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pulse.charts.map((chart, index) => (
                  <ChartBlock key={index} chart={chart} index={index} />
                ))}
              </div>
            )}

            {/* Insights */}
            {pulse.insights?.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-heading text-slate-900 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Key Insights
                </h2>
                <div className="space-y-4">
                  {pulse.insights.map((insight, index) => (
                    <InsightBlock key={index} insight={insight} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Footer & CTA */}
            {(pulse.footer_text || pulse.cta_label) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="p-8 bg-gradient-to-br from-evergreen-950 to-slate-900 rounded-2xl text-center"
              >
                {pulse.footer_text && (
                  <p className="text-lime-100 mb-6 text-lg">{pulse.footer_text}</p>
                )}
                
                {pulse.cta_label && pulse.cta_url && (
                  <Button 
                    onClick={handleCTA}
                    size="lg"
                    className="bg-lime-500 hover:bg-lime-400 text-evergreen-950 font-semibold"
                    data-testid="market-pulse-cta"
                  >
                    {pulse.cta_label}
                    <ChevronRight className="h-5 w-5 ml-2" />
                  </Button>
                )}
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex justify-center pt-4">
              <Link to="/dashboard">
                <Button variant="outline" size="lg">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
